import { getSavedLabelsUrl, prontoProductsUrl } from '../../utils/ConstantUtils'
import { saveLabelsUrl } from '../../../src/utils/ConstantUtils'
import { getApiInstance } from '../axiosInterceptor'
import { updateTokenUsageTime } from '../../utils/storageUtils'
import { generateProntoToken } from '../prontoService/prontoServiceUtil'

/**
 * Handles generating shelf edge labels
 * @param {Object} instance - MSAL instance
 * @param {Function} setProductData - State setter for product data
 * @param {Array} selectedRows - Selected row items with APNs and quantities
 * @param {Boolean} isCognitoUser - Flag indicating if user is authenticated with Cognito
 * @returns {Promise<Object|null>} - API response or null if failed
 */
export const handleGenerateLabelsUtil = async (instance, setProductData, selectedRows) => {
	// Pass the isCognitoUser flag to get the appropriate token
	const token = await generateProntoToken(instance)
	let productInfo = null

	if (token) {
		productInfo = await fetchProductInformation(instance, token, selectedRows)
		if (productInfo && Array.isArray(productInfo.data.productInfo)) {
			// Set product data if needed
			if (setProductData) {
				setProductData(productInfo.data.productInfo)
			}
		} else {
			console.error('Error while reading product info ', productInfo)
		}
	} else {
		console.error('Failed to generate Pronto token')
	}
	return productInfo
}

/**
 * Fetches product information from the API
 * @param {Object} instance - MSAL instance
 * @param {string} token - Pronto token
 * @param {Array} selectedRows - Selected row items with APNs
 * @param {Boolean} isCognitoUser - Flag indicating if user is authenticated with Cognito
 * @returns {Promise<Object|null>} - API response or null if failed
 */
const fetchProductInformation = async (instance, token, selectedRows) => {
	try {
		// Extract APNs from the selected rows
		const apns = selectedRows.map((item) => item.apn)
		// For AD users, use the existing approach with interceptors
		const response = await getApiInstance(instance).post(
			prontoProductsUrl,
			{
				apn: apns,
				requestType: 'shelfEdgeLabels'
			},
			{
				headers: {
					'X-Pronto-Token': `${token}`
				}
			}
		)

		if (response.status === 200) {
			updateTokenUsageTime()
			return response
		} else {
			throw new Error('Failed to fetch product information')
		}
	} catch (error) {
		console.error('Error fetching product information:', error)
		return null
	}
}

/**
 * Fetches requested items data for saved labels
 * @param {Object} instance - MSAL instance
 * @param {Function} setRows - State setter for rows
 * @param {Function} setIsReloading - State setter for loading state
 * @param {Boolean} isCognitoUser - Flag indicating if user is authenticated with Cognito
 * @returns {Promise<void>}
 */
export const fetchRequestedItemsData = async (instance, setRows, setIsReloading) => {
	setIsReloading(true)
	try {
		const response = await getApiInstance(instance).get(getSavedLabelsUrl)
		setRows(response.data.shelfEdgeReports)
	} catch (error) {
		console.error('Error fetching Requested Items:', error)
		throw error
	} finally {
		setIsReloading(false)
	}
}

/**
 * Saves or updates a label request
 * @param {Object} data - Request data
 * @param {Object} instance - MSAL instance
 * @param {string} requestType - Type of request (update or delete)
 * @param {Boolean} isCognitoUser - Flag indicating if user is authenticated with Cognito
 * @returns {Promise<Object>} - API response
 */
export const saveLabelRequest = async (data, instance, requestType) => {
	let params = {}
	if (requestType === 'delete') {
		params = {
			id: data.id,
			requestType: requestType
		}
	} else if (requestType === 'update') {
		params = {
			id: data.id,
			listName: data.listName,
			printList: data.printList || [],
			createdBy: data.createdBy,
			createdDate: new Date().toISOString(),
			requestType: requestType
		}
	}

	try {
		const response = await getApiInstance(instance).post(saveLabelsUrl, params)
		if (response.status === 200) {
			return response
		}
	} catch (error) {
		console.error('Error making POST request:', error)
		throw error
	}
}